import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  Type,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from '../../core/services/dialog';
import { AlertsListComponent } from '../alerts/alerts-list/alerts-list.component';
import { Subscription } from 'rxjs';
import { AlertService } from '../../core/services/alert/alert.service';
import { WebSockerSerive } from '../../core/services/socket/socket.service';
import { ToastService } from '../../core/services/toast/toast.service';
import { ActivityItemComponent } from '../../shared/components/activity-item-notification/activity-item.component';
import { TextButton } from '../../shared/components/buttons/text-button/text-button';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { IcAbove150EuroComponent } from '../../shared/components/Icons/alert-icons/ic-above-150euro/ic-above-150euro';
import { IcControlAlertComponent } from '../../shared/components/Icons/alert-icons/ic-control-alert/ic-control-alert';
import { IcInvalidErrorComponent } from '../../shared/components/Icons/alert-icons/ic-invalid-error/ic-invalid-error';
import { IcLowCreditComponent } from '../../shared/components/Icons/alert-icons/ic-low-credit/ic-low-credit';
import { IcNoReleaseComponent } from '../../shared/components/Icons/alert-icons/ic-no-release/ic-no-release';
import { IcNoRepresentation25Component } from '../../shared/components/Icons/alert-icons/ic-no-representation-25/ic-no-representation-25';
import { IcNoResponseComponent } from '../../shared/components/Icons/alert-icons/ic-no-response/ic-no-response';
import { Alert, AlertType, ModelName } from '../../shared/interfaces';
import { ActivityItem } from '../../shared/models/activity-item.model';
import { TranslatePipe } from '../../shared/pipes/translate/translate.pipe';

interface AlertUIConfig {
  color: string; // Tailwind / CSS class for color
  icon: Type<unknown>; // Angular component (your icon)
  route: string; // Base route to navigate
  startColor: string; // Tailwind / CSS class for start color
  endColor: string; // Tailwind / CSS class for end color
}

@Component({
  selector: 'app-updates',
  standalone: true,
  imports: [
    ActivityItemComponent,
    TranslatePipe,
    TextButton,
    EmptyStateComponent,
    CommonModule,
  ],
  templateUrl: './updates-component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'flex flex-col h-full overflow-hidden',
  },
})
export class UpdatesComponent implements OnInit, OnDestroy {
  activityItems = signal<ActivityItem<Alert>[]>([]);

  @Input() refreshUnreadCallback!: () => void;

  /**
   * Indicates whether the component is embedded inside a combined tab interface.
   */
  @Input() isEmbedded = false;

  page = 1;
  isLoading = false;
  hasMore = true; // stop calling API when no more data
  public totalUnreadCount = signal<number>(0);

  public translate = inject(TranslatePipe);
  private router = inject(Router);
  private dialogRef = inject(DynamicDialogRef);
  private dialogService = inject(DialogService);
  private alertService = inject(AlertService);
  private socketService = inject(WebSockerSerive);
  private toastService = inject(ToastService);

  private alertSocketSub?: Subscription;

  private alertUIConfig: Record<AlertType, AlertUIConfig> = {
    [AlertType.NO_PRESENTATION_25_DAYS]: {
      color: 'text-red-500',
      icon: IcNoRepresentation25Component,
      route: '/shipments/details',
      startColor: '#0091FF',
      endColor: '#0091FF',
    },
    [AlertType.CONTROL_ALERT]: {
      color: 'text-yellow-500',
      icon: IcControlAlertComponent,
      route: '/shipments/details',
      startColor: '#FCBE0D',
      endColor: '#FD6129',
    },
    [AlertType.NO_RESPONSE_15_MINUTES]: {
      color: 'text-orange-500',
      icon: IcNoResponseComponent,
      route: '/shipments/details',
      startColor: '#D78316',
      endColor: '#E8B51E',
    },
    [AlertType.INVALID_CC456_ERROR]: {
      color: 'text-purple-500',
      icon: IcInvalidErrorComponent,
      route: '/shipments/details',
      startColor: '#FFA25B',
      endColor: '#F43924',
    },
    [AlertType.XMLCNT_ERROR]: {
      color: 'text-pink-500',
      icon: IcInvalidErrorComponent,
      route: '/shipments/details',
      startColor: '#FFA25B',
      endColor: '#F43924',
    },
    [AlertType.NO_RELEASE]: {
      color: 'text-gray-500',
      icon: IcNoReleaseComponent,
      route: '/shipments/details',
      startColor: '#B957F1',
      endColor: '#E7229B',
    },
    [AlertType.LOW_CREDIT]: {
      color: 'text-blue-500',
      icon: IcLowCreditComponent,
      route: '/shipments/details',
      startColor: '#8F7DF5',
      endColor: '#282BCA',
    },
    [AlertType.DECO_150_EURO]: {
      color: 'text-green-500',
      icon: IcAbove150EuroComponent,
      route: '/shipments/details',
      startColor: '#FB6FBB',
      endColor: '#CD609B',
    },
    [AlertType.SHIPMENT_TOTALS_MISMATCH]: {
      color: 'text-red-500',
      icon: IcControlAlertComponent,
      route: '/shipments/details',
      startColor: '#FD6129',
      endColor: '#F43924',
    },
    [AlertType.EMCS_FOLLOW_UP_DUE]: {
      color: 'text-orange-500',
      icon: IcControlAlertComponent,
      route: '/emcs',
      startColor: '#D78316',
      endColor: '#E8B51E',
    },
    [AlertType.TRANSIT_DEADLINE]: {
      color: 'text-amber-500',
      icon: IcControlAlertComponent,
      route: '/transit-declarations/transit-departure/details',
      startColor: '#F59E0B',
      endColor: '#D97706',
    },
  };

  groupedAlerts = computed(() => {
    const items = this.activityItems();
    if (!items.length) return [];

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: {
      title: string;
      unreadCount: number;
      items: ActivityItem<Alert>[];
    }[] = [
      { title: 'today', unreadCount: 0, items: [] },
      { title: 'yesterday', unreadCount: 0, items: [] },
      { title: 'older', unreadCount: 0, items: [] },
    ];

    items.forEach((item) => {
      const itemDate = new Date(item.date);
      const itemDay = new Date(
        itemDate.getFullYear(),
        itemDate.getMonth(),
        itemDate.getDate()
      );

      let groupIndex = 2; // Older
      if (itemDay.getTime() === today.getTime()) {
        groupIndex = 0;
      } else if (itemDay.getTime() === yesterday.getTime()) {
        groupIndex = 1;
      }

      groups[groupIndex].items.push(item);
      if (!item.isRead) {
        groups[groupIndex].unreadCount++;
      }
    });

    return groups.filter((g) => g.items.length > 0);
  });

  onViewAll() {
    this.dialogService.open(AlertsListComponent, {
      header: this.translate.transform('alerts'),
      width: '80%',
      height: '90%',
      baseZIndex: 10001,
      modal: true,
      dismissableMask: true,
      closable: true,
      styleClass: 'custom-header',
    });
    this.onClose();
  }

  onClose() {
    this.dialogRef.close();
  }

  onScroll(event: Event) {
    const target = event.target as HTMLElement;

    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Trigger API call when near bottom (e.g., 100px before end)
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      this.loadMore();
    }
  }

  loadMore() {
    if (this.isLoading || !this.hasMore) return;
    this.page++;
    this.loadActivityItems();
  }

  ngOnInit(): void {
    this.loadActivityItems();

    // When a new alert arrives, prepend it to the list (no list clear) and show a toast
    this.alertSocketSub = this.socketService
      .listen<Alert | { Alert?: Alert; id?: number | string; isRead?: boolean }>(
        'alertCreated'
      )
      .subscribe(
        (
          data:
            | Alert
            | { Alert?: Alert; id?: number | string; isRead?: boolean }
        ) => {
          const d = data as {
            Alert?: Alert;
            id?: number | string;
            isRead?: boolean;
          };
          const alert: Alert | undefined = d.Alert ?? (data as Alert);
          if (alert) {
            // Normalize to Alert with flattened state
            const normalizedAlert: Alert = {
              ...alert,
              instanceId: data?.id ?? alert.id,
              isRead: data?.isRead ?? false,
            };
            const newItem = this.mapAlertToActivityItem(normalizedAlert);
            this.activityItems.update((current) => [newItem, ...current]);
            this.totalUnreadCount.update((count) => count + 1);
            this.toastService.showWarning(
              alert.title ?? 'New Alert',
              alert.message ?? ''
            );
          }
        }
      );
  }

  ngOnDestroy(): void {
    this.alertSocketSub?.unsubscribe();
  }

  loadActivityItems() {
    if (this.isLoading || !this.hasMore) return;
    this.isLoading = true;

    this.alertService.getAlerts().subscribe((res) => {
      this.isLoading = false;
      if (res.success && res.data?.result) {
        const newItems = res.data.result.flatMap((instance) => {
          const alert = instance.Alert;
          if (!alert) return [];
          const normalizedAlert: Alert = {
            ...alert,
            instanceId: instance.id,
            isRead: instance.isRead,
          };
          const activityItem = this.mapAlertToActivityItem(normalizedAlert);
          activityItem.data = normalizedAlert;
          return [activityItem];
        });

        if (newItems.length === 0) {
          this.hasMore = false;
        }

        this.activityItems.update((current) => [...current, ...newItems]);
        this.totalUnreadCount.set(
          this.activityItems().filter((a) => !a.isRead).length
        );
      }
    });
  }

  /** Convert Alert to ActivityItem */
  mapAlertToActivityItem(alert: Alert): ActivityItem<Alert> {
    const cfg = this.alertUIConfig[alert.type] ?? {
      color: 'text-gray-400',
      icon: IcAbove150EuroComponent,
      route: '/alerts',
      startColor: 'bg-red-100',
      endColor: 'bg-red-500',
    };
    return {
      id: alert.instanceId ?? alert.id,
      instanceId: alert.instanceId,
      title: alert.title || 'Alert',
      content: alert.message ?? '',
      date: alert.createdAt ? new Date(alert.createdAt) : new Date(),
      icon: cfg.icon,
      startColor: cfg.startColor,
      endColor: cfg.endColor,
      hasLink: true,
      isRead: alert.isRead ?? false,
      type: alert.type,
      data: alert,
    };
  }

  public async markAsRead(
    alertId: string | number,
    instanceId: string | number
  ) {
    this.alertService.markRead(Number(alertId)).then((res) => {
      if (res.success) {
        // Local update
        this.activityItems.update((items: ActivityItem<Alert>[]) =>
          items.map((ai: ActivityItem<Alert>) =>
            ai.instanceId === instanceId ? { ...ai, isRead: true } : ai
          )
        );
        this.totalUnreadCount.set(
          this.activityItems().filter((a: ActivityItem<Alert>) => !a.isRead)
            .length
        );
      }
    });
  }

  onAlertClick(item: ActivityItem<Alert>) {
    const alert = item.data;
    if (!alert) return;

    if (!item.isRead && item.instanceId && alert?.id) {
      this.markAsRead(alert.id, item.instanceId);
    }

    const cfg = this.getUIConfig(alert.type);
    const metadata = (alert.metadata as Record<string, unknown>) ?? {};
    const shipmentId = metadata['shipmentId'];
    const transitDeclarationId =
      metadata['transitDeclarationId'] ??
      metadata['transitId'] ??
      (alert.modelName === ModelName.transitDeclaration ||
      alert.type === AlertType.TRANSIT_DEADLINE
        ? alert.modelId ?? metadata['id']
        : undefined);

    if (transitDeclarationId) {
      const transitType = String(metadata['transitType'] ?? '').toLowerCase();
      const isArrival =
        transitType === 'arrival' || transitType === 'transit-arrival';
      const baseRoute = isArrival
        ? '/transit-declarations/transit-arrival/details'
        : '/transit-declarations/transit-departure/details';
      this.router.navigate([`${baseRoute}/${transitDeclarationId}`]);
    } else if (shipmentId) {
      this.router.navigate([`${cfg.route}/${shipmentId}`]);
    } else {
      const filters = {
        'Alerts.type': alert.type,
      };
      const fallbackRoute =
        alert.type === AlertType.TRANSIT_DEADLINE
          ? '/transit-declarations/transit-departure'
          : cfg.route;
      this.router.navigate([fallbackRoute], {
        queryParams: {
          tab: 'declarations',
        },
        state: {
          fromAlerts: true,
          specificValueFields: JSON.stringify(filters),
        },
      });
    }
    this.onClose();
  }

  onMarkAllAsRead() {
    const unreadAlertIds = this.activityItems()
      .filter((a) => !a.isRead)
      .map((a) => a.instanceId ?? a.id);

    if (unreadAlertIds.length === 0) return;

    this.alertService.bulkMarkRead({ alertIds: unreadAlertIds });

    this.activityItems.update((current) =>
      current.map((a) => ({ ...a, isRead: true }))
    );
    this.totalUnreadCount.set(0);

    if (this.refreshUnreadCallback) {
      this.refreshUnreadCallback();
    }
  }

  getUIConfig(type: AlertType): AlertUIConfig {
    return (
      this.alertUIConfig[type] ?? {
        color: 'text-gray-400',
        icon: 'pi pi-info-circle',
        route: '/alerts',
      }
    );
  }
}
